'use client';

import { useState, useCallback } from 'react';
import { apiFetch } from '@lib/apiFetch';
import { audioInterviewConfig } from '../config/audioInterviewConfig';

interface UseElevenLabsTokenReturn {
  conversationToken: string | null;
  isLoading: boolean;
  error: string | null;
  fetchToken: () => Promise<string | null>;
}

/**
 * Hook to fetch ElevenLabs conversation token
 *
 * Fetches a conversation token from our backend for the configured ElevenLabs agent.
 * Backend endpoint: `v1/interview/elevenlabs/token/{agentId}` returning `{ token: string }`.
 * This token is then used to start a conversation with the ElevenLabs agent.
 */
export const useElevenLabsToken = (locale: Locale): UseElevenLabsTokenReturn => {
  const [conversationToken, setConversationToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchToken = useCallback(async (): Promise<string | null> => {
    setIsLoading(true);
    setError(null);

    const agentId = encodeURIComponent(audioInterviewConfig.elevenlabs.agentId);

    try {
      const { data } = await apiFetch('/v1/interview/elevenlabs/token/{agentId}', {
        method: 'get',
        locale,
        pathParams: {
          agentId,
        },
      });

      if (!data?.token) {
        throw new Error('Failed to get conversation token');
      }

      setConversationToken(data.token);
      setIsLoading(false);
      return data.token;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      setIsLoading(false);
      return null;
    }
  }, [locale]);

  return {
    conversationToken,
    isLoading,
    error,
    fetchToken,
  };
};
